import { ChangeDetectionStrategy, Component, effect, inject, input, OnDestroy, signal, untracked } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { CharacterCounterComponent } from '../../miscs';
import { FormErrorComponent } from '../form-error/form-error.component';
import { FormLabelComponent } from '../form-label/form-label.component';

@Component({
    selector: 'textarea-fc',
    standalone: true,
    imports: [
        FormLabelComponent,
        TranslateModule,
        ReactiveFormsModule,
        TooltipModule,
        TextareaModule,
        FormErrorComponent,
        CharacterCounterComponent,
    ],
    templateUrl: './textarea-fc.component.html',
    styleUrl: './textarea-fc.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaFcComponent implements OnDestroy {
    private translate = inject(TranslateService);

    id = input.required<string>();
    control = input.required<FormControl>();

    label = input<string>('');
    tooltipText = input<string>('');
    required = input<boolean>(false);

    placeholder = input<string>('uiKit.form.textarea.placeholder');
    name = input<string>(''); //used for validation messages, if there is no label
    rows = input<number>(5);
    maxLength = input<number>(5000);
    size = input<'small' | 'large'>('large');

    textValue = signal<string>('');

    private subscription = new Subscription();

    constructor() {
        effect(() => {
            const currentControl = this.control();
            this.subscription.unsubscribe();
            this.subscription = new Subscription();

            untracked(() => {
                this.textValue.set(currentControl.value || '');
                this.subscription.add(currentControl.valueChanges.subscribe(value => this.textValue.set(value || '')));
            });
        });
    }

    readonly errorMessages: Record<string, (label: string, control: FormControl, error: any) => string> = {
        maxlength: (label, control) =>
            `${label} ${this.translate.instant('uiKit.form.textarea.maxLengthFormValidator', {
                limit: control.errors?.['maxlength'].requiredLength,
            })}`,
        minlength: (label, control) =>
            `${label} ${this.translate.instant('uiKit.form.textarea.minLengthFormValidator', {
                limit: control.errors?.['minlength'].requiredLength,
            })}`,
        emptyInput: label => `${this.translate.instant('uiKit.form.textarea.emptyInputFormValidator')} ${label}`,
        required: label => `${this.translate.instant('uiKit.form.textarea.emptyInputFormValidator')} ${label}`,
    };

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    getErrorKeys(errors: ValidationErrors | null): string[] {
        return errors ? Object.keys(errors) : [];
    }

    getErrorMessage(error: string): string {
        const label = this.label() ? this.label() : this.name();

        return this.errorMessages[error]
            ? this.errorMessages[error](
                  this.translate.instant(label),
                  this.control(),
                  this.control().errors && this.control().errors?.[error]
              )
            : '';
    }
}
